import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';

interface QuickStartState {
  isActive: boolean;
  stepsCompleted: {
    orgConnected: boolean;
    viewedOrgs: boolean;
    exploredDashboard: boolean;
    viewedFlows: boolean;
    viewedFlowDetail: boolean;
    viewedFields: boolean;
    viewedEntryCriteria: boolean;
  };
  isDismissed: boolean;
}

interface QuickStartContextType {
  isActive: boolean;
  stepsCompleted: {
    orgConnected: boolean;
    viewedOrgs: boolean;
    exploredDashboard: boolean;
    viewedFlows: boolean;
    viewedFlowDetail: boolean;
    viewedFields: boolean;
    viewedEntryCriteria: boolean;
  };
  currentStep: number;
  allStepsComplete: boolean;
  isDismissed: boolean;
  markStepComplete: (step: 'orgConnected' | 'viewedOrgs' | 'exploredDashboard' | 'viewedFlows' | 'viewedFlowDetail' | 'viewedFields' | 'viewedEntryCriteria') => void;
  dismissQuickStart: () => void;
  resetQuickStart: () => void;
  activateQuickStart: () => void;
}

const QuickStartContext = createContext<QuickStartContextType | undefined>(undefined);

const STORAGE_KEY_PREFIX = 'quickStartState';

const defaultState: QuickStartState = {
  isActive: true,
  stepsCompleted: {
    orgConnected: false,
    viewedOrgs: false,
    exploredDashboard: false,
    viewedFlows: false,
    viewedFlowDetail: false,
    viewedFields: false,
    viewedEntryCriteria: false
  },
  isDismissed: false
};

export function QuickStartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<QuickStartState>(defaultState);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get storage key for current user
  const getStorageKey = () => {
    return user?.id ? `${STORAGE_KEY_PREFIX}_${user.id}` : STORAGE_KEY_PREFIX;
  };

  // Load state from localStorage when user changes or on mount
  useEffect(() => {
    if (user?.id) {
      // User logged in - load their specific state
      const storageKey = getStorageKey();
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        try {
          setState(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse Quick Start state:', e);
          setState(defaultState);
        }
      } else {
        // New user or first time - use default state
        setState(defaultState);
      }

      setCurrentUserId(user.id);
    } else {
      // No user - reset to default
      setState(defaultState);
      setCurrentUserId(null);
    }
  }, [user?.id]);

  // Save to localStorage whenever state changes (only if user is logged in)
  useEffect(() => {
    if (user?.id) {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    }
  }, [state, user?.id]);

  const markStepComplete = useCallback((step: 'orgConnected' | 'viewedOrgs' | 'exploredDashboard' | 'viewedFlows' | 'viewedFlowDetail' | 'viewedFields' | 'viewedEntryCriteria') => {
    setState(prev => ({
      ...prev,
      stepsCompleted: {
        ...prev.stepsCompleted,
        [step]: true
      }
    }));
  }, []);

  const dismissQuickStart = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDismissed: true,
      isActive: false
    }));
  }, []);

  const activateQuickStart = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: true,
      isDismissed: false
    }));
  }, []);

  const resetQuickStart = useCallback(() => {
    setState(defaultState);
  }, []);

  // Calculate current step (1-based) - find first incomplete step
  const currentStep = useMemo(() => {
    return !state.stepsCompleted.orgConnected ? 1 :
      !state.stepsCompleted.viewedOrgs ? 2 :
      !state.stepsCompleted.exploredDashboard ? 3 :
      !state.stepsCompleted.viewedFlows ? 4 :
      !state.stepsCompleted.viewedFlowDetail ? 5 :
      !state.stepsCompleted.viewedFields ? 6 :
      !state.stepsCompleted.viewedEntryCriteria ? 7 :
      8; // All done
  }, [state.stepsCompleted]);

  const allStepsComplete = useMemo(() => {
    return state.stepsCompleted.orgConnected &&
      state.stepsCompleted.viewedOrgs &&
      state.stepsCompleted.exploredDashboard &&
      state.stepsCompleted.viewedFlows &&
      state.stepsCompleted.viewedFlowDetail &&
      state.stepsCompleted.viewedFields &&
      state.stepsCompleted.viewedEntryCriteria;
  }, [state.stepsCompleted]);

  const value: QuickStartContextType = useMemo(() => ({
    isActive: state.isActive,  // Stay active until user dismisses
    stepsCompleted: state.stepsCompleted,
    currentStep,
    allStepsComplete,
    isDismissed: state.isDismissed,
    markStepComplete,
    dismissQuickStart,
    resetQuickStart,
    activateQuickStart
  }), [state.isActive, state.stepsCompleted, state.isDismissed, currentStep, allStepsComplete,
      markStepComplete, dismissQuickStart, resetQuickStart, activateQuickStart]);

  return (
    <QuickStartContext.Provider value={value}>
      {children}
    </QuickStartContext.Provider>
  );
}

export function useQuickStart() {
  const context = useContext(QuickStartContext);
  if (context === undefined) {
    throw new Error('useQuickStart must be used within a QuickStartProvider');
  }
  return context;
}
